import { ChangeEvent, memo, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IInputFile, UploadFileService, useFormContext } from "@4uhub/lib4uhub";

import { GlobalAxios } from "../../../../../axios/axios-config";
import { TRegisterForm } from "../RegisterSchema";
import { getFileSrc } from "./SiteAppChatProfilePreview";

const FILE_ROUTE = process.env.REACT_APP_FILEMANAGER + "/api/v1/Upload";
const ACCEPT = ".jpg, .jpeg, .png, .webp";
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 2 * 1_000_000;

const translationPath = "page.register.applications.";

interface ISiteAppChatProfileFileProps {
  name: "chatLogo" | "chatBotAvatar";
  label: string;
  placeholder: string;
  mediaTypeCode: string;
  variant: "rounded" | "circular";
  onDeleteFileHandler: (file: IInputFile) => void;
}

const SiteAppChatProfileFile = ({
  name,
  label,
  placeholder,
  mediaTypeCode,
  variant,
  onDeleteFileHandler,
}: ISiteAppChatProfileFileProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TRegisterForm>();
  const {
    field: { value, onChange },
  } = useController({ control, name });

  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(
    () => new UploadFileService(GlobalAxios, FILE_ROUTE),
    []
  );

  const file = value as IInputFile | null | undefined;
  const src = useMemo(() => getFileSrc(file), [file]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;

    if (
      !ACCEPTED_TYPES.includes(selected.type) ||
      selected.size >= MAX_FILE_SIZE
    ) {
      setError(t(translationPath + "chat_file_invalid"));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const { data } = await service.upload({
        data: {
          type: "Public",
          parentName: "SiteApp",
          mediaTypeIdentifier: "SiteAppMediaType",
          mediaTypeCode,
          file: selected,
        },
        progressCallback: () => {},
      });

      if (file?.id) onDeleteFileHandler(file);

      onChange({
        id: data.id,
        baseUrl: data.baseUrl,
        isPublic: true,
        fileName: selected.name,
        filePath: "",
        cdnDomain: "",
        mimeType: selected.type,
        fileSize: selected.size,
        file: selected,
      });
    } catch {
      setError(t(translationPath + "chat_file_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    if (file?.id) onDeleteFileHandler(file);
    onChange(null);
  };

  return (
    <Box
      component="fieldset"
      sx={{
        m: 0,
        px: 1.5,
        pt: 0.5,
        pb: 1.5,
        minWidth: 0,
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "background.paper",
      }}
    >
      <Typography
        component="legend"
        variant="caption"
        color="text.secondary"
        sx={{ px: 0.5 }}
      >
        {label}
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          variant={variant}
          src={src}
          sx={{
            width: 40,
            height: 40,
            fontSize: 12,
            bgcolor: "action.selected",
            color: "text.secondary",
          }}
        >
          {placeholder}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="div" noWrap>
            {file?.fileName || t(translationPath + "chat_file_empty")}
          </Typography>
          <Typography
            component="div"
            variant="caption"
            color="text.secondary"
          >
            {ACCEPT}
          </Typography>
        </Box>

        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {file?.id && (
              <IconButton size="small" onClick={handleRemove}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            <Button
              variant="outlined"
              onClick={() => inputRef.current?.click()}
            >
              {t(
                translationPath +
                  (file?.id ? "chat_file_change" : "chat_file_select")
              )}
            </Button>
          </>
        )}
      </Stack>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={ACCEPT}
        onChange={handleFileChange}
      />

      {error && (
        <Typography variant="caption" color="error" component="p" mt={1}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default memo(SiteAppChatProfileFile);
